import { FormHelperTextProps, InputBaseComponentProps, InputLabelProps } from '@mui/material';
import TextField from '@mui/material/TextField';
import { SxProps } from '@mui/system';
import { ChangeEvent, ClipboardEvent, FC, KeyboardEvent, useEffect, useState, type JSX } from 'react';

interface Props {
  autoComplete?: string;
  blurOnEnter?: boolean;
  'data-testid'?: string;
  disabled?: boolean;
  endAdornment?: JSX.Element;
  error?: boolean;
  FormHelperTextProps?: FormHelperTextProps & { 'data-testid'?: string };
  helperText?: string;
  inputProps?: InputBaseComponentProps;
  InputLabelProps?: InputLabelProps;
  label?: string;
  multiline?: boolean;
  onChange?: (value: string) => Promise<void>;
  onInputChange?: (value: string) => void;
  onKeyUp?: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  rows?: number;
  size?: 'small' | 'medium';
  sx?: SxProps;
  trimOnPaste?: boolean;
  type?: string;
  value: string;
}

export const TextEditor: FC<Props> = ({
  autoComplete,
  blurOnEnter = true,
  disabled = false,
  endAdornment,
  error: propsError,
  helperText,
  inputProps,
  label,
  multiline,
  onChange,
  onInputChange,
  onKeyUp,
  placeholder,
  required,
  rows,
  size,
  sx,
  trimOnPaste,
  type,
  value: initialValue,
  ...props
}) => {
  const [error, setError] = useState<Error>();
  const [isLoading, setIsLoading] = useState(false);
  const [lastSavedValue, setLastSavedValue] = useState<string>(initialValue);
  const [value, setValue] = useState<string>(initialValue);

  useEffect(() => {
    setValue(initialValue);
    setLastSavedValue(initialValue);
  }, [initialValue]);

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    const target = event.target as HTMLInputElement;
    if (blurOnEnter && event.key === 'Enter' && !multiline) {
      target.blur();
    }
  };

  const onInputKeyUp = (event: KeyboardEvent<HTMLInputElement>): void => {
    const target = event.target as HTMLInputElement;
    onKeyUp?.(target.value);
  };

  const onValueInputBlur = (): void => {
    if (lastSavedValue === value || !onChange) return;

    setIsLoading(true);
    void onChange(value)
      .then(() => {
        setLastSavedValue(value);
      }).catch().finally(() => {
        setIsLoading(false);
      });
  };

  const setValueAndError = (value: string): void => {
    setValue(value);
    onInputChange?.(value);
    if (required && !value) return setError(new Error());
    setError(undefined);
  };

  const onValueChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const inputValue = event.target.value;
    setValueAndError(inputValue);
  };

  const onPaste = (event: ClipboardEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');
    const trimmedText = pastedText.trim();

    const input = event.target as HTMLInputElement;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;

    const newValue = value.slice(0, start) + trimmedText + value.slice(end);

    setValueAndError(newValue);
  };

  return (
    <TextField
      autoComplete={autoComplete}
      disabled={disabled || isLoading}
      error={propsError ?? !!error}
      helperText={helperText}
      label={label}
      multiline={multiline}
      onBlur={onValueInputBlur}
      onChange={onValueChange}
      onKeyDown={onInputKeyDown}
      onKeyUp={onInputKeyUp}
      onPaste={trimOnPaste ? onPaste : undefined}
      placeholder={placeholder}
      required={required}
      rows={rows}
      size={size}
      sx={sx}
      type={type ?? 'text'}
      value={value}
      inputProps={inputProps}
      InputProps={endAdornment ? { endAdornment } : undefined}
      {...props}
    />
  );
};
